import React, { useMemo, useState } from "react";
import { MdLock, MdWarningAmber } from "react-icons/md";
import Card from "../../../../components/card";
import { AlcancePermissao, PermissionInfo, Profile } from "../../../../interfaces/Profile";
import AlcanceBadge from "./AlcanceBadge";
import { usePermissions } from "../../../../auth/PermissionProvider";
import { Permissions } from "../../../../auth/permissions";

interface Props {
  profile: Profile;
  catalog: PermissionInfo[];
  onSave: (changes: {
    name: string;
    description: string | null;
    permissions: string[];
  }) => Promise<void>;
}

/** Ordem e títulos das seções. Sem `alcance` no catálogo (backend antigo) vira uma seção só, sem título. */
const SECOES: { alcance: AlcancePermissao; titulo: string }[] = [
  { alcance: "interna", titulo: "Permissões internas (equipe DataValue)" },
  { alcance: "externa", titulo: "Permissões externas (clientes — valem na organização do vínculo)" },
  { alcance: "ambas", titulo: "Comuns (internas e externas)" },
];

const porModulo = (items: PermissionInfo[]) => {
  const byModule = new Map<string, PermissionInfo[]>();
  items.forEach((p) => {
    const list = byModule.get(p.module) ?? [];
    list.push(p);
    byModule.set(p.module, list);
  });
  return Array.from(byModule.entries());
};

/**
 * A matriz do perfil: agrupada por módulo, porque é assim que o gestor pensa ("o financeiro
 * pode…"), não por ordem alfabética de chave técnica.
 */
const ProfileEditor: React.FC<Props> = ({ profile, catalog, onSave }) => {
  const { permissions: mine } = usePermissions();

  const [name, setName] = useState(profile.name);
  const [description, setDescription] = useState(profile.description ?? "");
  const [selected, setSelected] = useState<Set<string>>(() => new Set(profile.permissions));
  const [saving, setSaving] = useState(false);

  const sections = useMemo(() => {
    if (!catalog.some((p) => p.alcance)) return [{ titulo: null, modules: porModulo(catalog) }];
    return SECOES.map(({ alcance, titulo }) => ({
      titulo,
      modules: porModulo(catalog.filter((p) => p.alcance === alcance)),
    })).filter((s) => s.modules.length > 0);
  }, [catalog]);

  const dirty =
    name !== profile.name ||
    description !== (profile.description ?? "") ||
    selected.size !== profile.permissions.length ||
    profile.permissions.some((p) => !selected.has(p));

  /**
   * O aviso que evita o tiro no pé clássico: tirar `profiles.manage` do perfil que o próprio
   * usuário tem e ficar sem como voltar a esta tela. Não bloqueia — pode ser exatamente o que se
   * quer, e há outros Admins — mas força a confirmação.
   */
  const lockingMyselfOut =
    mine.has(Permissions.ProfilesManage) &&
    profile.permissions.includes(Permissions.ProfilesManage) &&
    !selected.has(Permissions.ProfilesManage);

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleModule = (items: PermissionInfo[]) => {
    const allOn = items.every((i) => selected.has(i.key));
    setSelected((prev) => {
      const next = new Set(prev);
      items.forEach((i) => (allOn ? next.delete(i.key) : next.add(i.key)));
      return next;
    });
  };

  const handleSave = async () => {
    if (lockingMyselfOut) {
      const ok = window.confirm(
        `Você está removendo "Gerenciar perfis de acesso" de um perfil que você mesmo tem. ` +
          `Se este for seu único acesso a esta tela, você não conseguirá voltar aqui. Continuar?`
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || null,
        permissions: Array.from(selected),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card extra="w-full px-6 py-5">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-100 pb-4 dark:border-white/10">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={profile.isSystem}
              className="w-full max-w-sm rounded-lg border border-transparent bg-transparent px-2 py-1 text-lg font-bold text-navy-700 outline-none focus:border-brand-500 disabled:cursor-not-allowed dark:text-white"
            />
            <AlcanceBadge alcance={profile.alcance} />
            {profile.isSystem && (
              <span className="flex shrink-0 items-center gap-1 rounded-lg bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 dark:bg-navy-900 dark:text-gray-400">
                <MdLock className="h-3.5 w-3.5" />
                Perfil de sistema
              </span>
            )}
          </div>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={profile.isSystem}
            placeholder="Descrição — para que serve este perfil"
            className="mt-1 w-full max-w-2xl rounded-lg border border-transparent bg-transparent px-2 py-1 text-sm text-gray-600 outline-none focus:border-brand-500 disabled:cursor-not-allowed dark:text-gray-400"
          />
          {profile.isSystem && (
            <p className="mt-1.5 px-2 text-xs text-gray-500 dark:text-gray-400">
              Nome e descrição são fixos: é o nome que liga este perfil ao papel de origem. As
              permissões, essas sim, são livres.
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {selected.size} de {catalog.length}
          </span>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving}
            className="rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-semibold text-white shadow-dv-glow transition-all hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100"
          >
            {saving ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>

      {lockingMyselfOut && (
        <div className="mt-4 flex items-start gap-2 rounded-xl bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
          <MdWarningAmber className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Você está tirando o acesso a esta tela de um perfil que você mesmo tem. Confirme que
            existe outro caminho de volta antes de salvar.
          </span>
        </div>
      )}

      {sections.map(({ titulo, modules }, idx) => (
        <section
          key={titulo ?? "todas"}
          aria-label={titulo ?? undefined}
          className={idx === 0 ? "mt-4" : "mt-6 border-t border-gray-100 pt-4 dark:border-white/10"}
        >
          {titulo && (
            <h2 className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {titulo}
            </h2>
          )}
          <div className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
            {modules.map(([module, items]) => {
              const onCount = items.filter((i) => selected.has(i.key)).length;
              return (
                <div key={module}>
                  <div className="mb-1.5 flex items-center justify-between gap-2 border-b border-gray-100 pb-1.5 dark:border-white/10">
                    <h3 className="text-sm font-bold text-navy-700 dark:text-white">{module}</h3>
                    <button
                      type="button"
                      onClick={() => toggleModule(items)}
                      className="text-xs font-medium text-brand-500 hover:underline"
                    >
                      {onCount === items.length ? "Desmarcar todos" : "Marcar todos"}
                    </button>
                  </div>
                  <ul className="flex flex-col">
                    {items.map((item) => (
                      <li key={item.key}>
                        <label className="flex cursor-pointer items-start gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-gray-50 dark:hover:bg-white/5">
                          <input
                            type="checkbox"
                            checked={selected.has(item.key)}
                            onChange={() => toggle(item.key)}
                            className="mt-0.5 h-4 w-4 shrink-0 accent-brand-500"
                          />
                          <span className="min-w-0">
                            <span className="block text-sm text-gray-700 dark:text-gray-300">
                              {item.description}
                            </span>
                            <code className="block text-[11px] text-gray-400 dark:text-gray-500">
                              {item.key}
                            </code>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </Card>
  );
};

export default ProfileEditor;
